import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { ApiError, apiUrl } from '@/api/client'
import { QueryState } from '@/components/QueryState'
import { UnsavedChangesDialog } from '@/components/UnsavedChanges'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { useAuth } from '@/features/auth/store'
import { DeliveryRates } from '@/features/company/DeliveryRates'
import {
  ACCRUAL_LABELS,
  COMMISSION_ACCRUALS,
  getCompanySettings,
  INDIA_GST,
  setCompanyLogo,
  stateOfRegistration,
  updateCompanySettings,
  type CompanySettings as Settings,
  type CompanySettingsInput,
} from '@/features/company/api'
import { useRegime } from '@/features/company/regime'
import { uploadFile } from '@/features/files/api'
import { useDirtyGuard } from '@/lib/dirtyGuard'
import { formatMoney } from '@/lib/money'
import { toast } from '@/lib/toast'

/**
 * The seller block: who the invoice is from, what jurisdiction it bills under, and what it
 * says at the bottom.
 *
 * These twenty-odd fields are copied onto every document at the moment it is raised, so a
 * change here shows on the next invoice and leaves every earlier one exactly as it was
 * printed. That is the point of the screen: branding without a redeploy.
 *
 * The tax regime is the same idea one level up (ADR-013). A deployment that turns out to
 * be American changes a dropdown rather than waiting for a redeploy, and everything that
 * depends on the answer -- what the registration box is called and what it accepts, which
 * regions the state list offers, whether a PAN is asked for at all -- follows the
 * selection as it is made, not after the server has refused the save.
 *
 * Under GST the registration is the authority on the state: a GSTIN is a state code
 * followed by a PAN, so the dropdown follows it and locks. Nothing in an EIN says where a
 * business is, so in the US the dropdown stands on its own.
 */

/**
 * Nothing in this schema transforms its input.
 *
 * The form holds strings and only strings; an empty box becomes `null` in `toPayload`, at
 * the edge, where it is one function anybody can read rather than a transform buried in a
 * field that the resolver's types would then have to thread back through the form.
 */
const optional = (max: number) =>
  z.string().trim().max(max, `That is longer than ${max} characters`)

const clean = (value: string) => value.replace(/\s+/g, '').toUpperCase()

/** A required amount box. The same three things the server checks: present, numeric, not negative. */
const amount = (blank: string) =>
  z
    .string()
    .trim()
    .min(1, blank)
    .refine((value) => Number.isFinite(Number(value)), { message: 'Enter a number' })
    .refine((value) => Number(value) >= 0, { message: 'That cannot be negative' })

/** The same, but an empty box is an answer in its own right rather than a missing one. */
const optionalAmount = z
  .string()
  .trim()
  .refine((value) => value === '' || Number.isFinite(Number(value)), { message: 'Enter a number' })
  .refine((value) => value === '' || Number(value) >= 0, { message: 'That cannot be negative' })

/** A GSTIN: two state digits, a PAN, an entity digit, the letter Z, a check character. */
const GSTIN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/
/** An EIN as the IRS writes it, or the nine bare digits somebody copies off a letter. */
const EIN = /^[0-9]{2}-?[0-9]{7}$/
/** A state seller's permit. Every state numbers these its own way; only the shape is checkable. */
const PERMIT = /^[A-Z0-9][A-Z0-9-]{4,19}$/

/**
 * What each jurisdiction accepts in the one registration box, and what to say when it does
 * not. Mirrors the server rule rather than replacing it: the API validates again, and this
 * is here so the answer comes back while the person still has the number in front of them.
 */
const REGISTRATION_RULES: Record<string, { ok: (value: string) => boolean; message: string }> = {
  india_gst: {
    ok: (value) => GSTIN.test(value),
    message: 'A GSTIN is 15 characters: 27AAPFU0939F1ZV',
  },
  us_sales_tax: {
    ok: (value) => EIN.test(value) || PERMIT.test(value),
    message:
      "An EIN is nine digits (12-3456789); a seller's permit is 5 to 20 of letters, digits or hyphens",
  },
}

const schema = z
  .object({
    legal_name: z.string().trim().min(2, 'Enter the registered name').max(200, 'That is too long'),
    trade_name: optional(200),
    tax_regime: z.string().min(1, 'Choose a tax regime'),
    tax_registration_id: z.string(),
    pan: z
      .string()
      .refine((value) => clean(value) === '' || /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(clean(value)), {
        message: 'A PAN is 10 characters: AAPFU0939F',
      }),
    state_code: z.string(),
    address_line1: optional(255),
    address_line2: optional(255),
    city: optional(100),
    postal_code: optional(20),
    country: z.string().trim().min(1, 'Enter a country').max(100, 'That is too long'),
    email: z
      .string()
      .refine(
        (value) => value.trim() === '' || z.string().email().safeParse(value.trim()).success,
        {
          message: 'That is not a valid email address',
        },
      ),
    phone: optional(30),
    website: optional(255),
    currency: z.string().refine((value) => clean(value).length === 3, {
      message: 'A currency code is three letters',
    }),
    invoice_terms: optional(4000),
    invoice_footer: optional(4000),
    bank_details: optional(4000),
    commission_rate_pct: z
      .string()
      .trim()
      .min(1, 'Enter a rate, or 0 for none')
      .refine((value) => Number.isFinite(Number(value)), { message: 'Enter a number' })
      .refine((value) => Number(value) >= 0 && Number(value) <= 100, {
        message: 'A rate is between 0 and 100',
      }),
    commission_accrual: z.enum(COMMISSION_ACCRUALS),
    shipping_flat_rate: amount('Enter a charge, or 0 for free delivery'),
    shipping_free_over: optionalAmount,
    // Not rendered anywhere on this form. It rides along because the PUT replaces the
    // whole record, and a field the payload leaves out is a field silently reset to its
    // default -- which for this one would quietly lower an approval threshold.
    stock_adjustment_approval_value: z.string(),
  })
  .superRefine((values, ctx) => {
    // Checked here rather than on the field, because what is valid depends on another
    // field: the same fifteen characters are a GSTIN in one jurisdiction and nothing at
    // all in the other, and zod cannot see a sibling from inside a field refinement.
    const registration = clean(values.tax_registration_id)
    const rule = REGISTRATION_RULES[values.tax_regime]
    if (registration && rule && !rule.ok(registration)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tax_registration_id'],
        message: rule.message,
      })
    }
  })

type FormValues = z.infer<typeof schema>

/** An empty box means "not set", which the API spells `null` rather than `""`. */
const orNull = (value: string) => value.trim() || null

function toPayload(values: FormValues): CompanySettingsInput {
  return {
    legal_name: values.legal_name.trim(),
    trade_name: orNull(values.trade_name),
    tax_regime: values.tax_regime,
    tax_registration_id: clean(values.tax_registration_id) || null,
    // Kept across a switch rather than cleared: a deployment that tries the US and comes
    // back should not have lost the PAN somebody typed in months ago.
    pan: clean(values.pan) || null,
    // A GSTIN wins where there is one; under any other regime the dropdown is all there
    // is. The server checks the pair again regardless.
    state_code:
      stateOfRegistration(values.tax_regime, values.tax_registration_id) ??
      orNull(values.state_code),
    address_line1: orNull(values.address_line1),
    address_line2: orNull(values.address_line2),
    city: orNull(values.city),
    postal_code: orNull(values.postal_code),
    country: values.country.trim(),
    email: orNull(values.email),
    phone: orNull(values.phone),
    website: orNull(values.website),
    currency: clean(values.currency),
    invoice_terms: orNull(values.invoice_terms),
    invoice_footer: orNull(values.invoice_footer),
    bank_details: orNull(values.bank_details),
    commission_rate_pct: values.commission_rate_pct.trim(),
    commission_accrual: values.commission_accrual,
    shipping_flat_rate: values.shipping_flat_rate.trim(),
    // An empty box means "the charge is never waived", which the API spells `null`. Zero
    // would mean the opposite -- every order over nothing ships free.
    shipping_free_over: orNull(values.shipping_free_over),
    stock_adjustment_approval_value: values.stock_adjustment_approval_value,
  }
}

function valuesOf(settings: Settings): FormValues {
  return {
    legal_name: settings.legal_name,
    trade_name: settings.trade_name ?? '',
    tax_regime: settings.tax_regime,
    tax_registration_id: settings.tax_registration_id ?? '',
    pan: settings.pan ?? '',
    state_code: settings.state_code ?? '',
    address_line1: settings.address_line1 ?? '',
    address_line2: settings.address_line2 ?? '',
    city: settings.city ?? '',
    postal_code: settings.postal_code ?? '',
    country: settings.country,
    email: settings.email ?? '',
    phone: settings.phone ?? '',
    website: settings.website ?? '',
    currency: settings.currency,
    invoice_terms: settings.invoice_terms ?? '',
    invoice_footer: settings.invoice_footer ?? '',
    bank_details: settings.bank_details ?? '',
    commission_rate_pct: settings.commission_rate_pct,
    commission_accrual: settings.commission_accrual,
    shipping_flat_rate: settings.shipping_flat_rate,
    shipping_free_over: settings.shipping_free_over ?? '',
    stock_adjustment_approval_value: settings.stock_adjustment_approval_value,
  }
}

const TEXTAREA =
  'w-full rounded-md border border-slate-300 bg-white p-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const SELECT =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'disabled:cursor-not-allowed disabled:opacity-60 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

export function CompanySettings() {
  const can = useAuth((s) => s.can)
  const canEdit = can('system_settings', 'update')
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)
  const logoInput = useRef<HTMLInputElement>(null)

  const query = useQuery({
    queryKey: ['company-settings'],
    queryFn: getCompanySettings,
    retry: false,
  })
  const settings = query.data

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: settings ? valuesOf(settings) : undefined,
  })

  // The selection, not the saved value: the labels and the state list have to change as
  // the dropdown is used, or the form spends the rest of the edit describing the country
  // the user has just switched away from.
  const chosenRegime = watch('tax_regime') || settings?.tax_regime
  const regime = useRegime(chosenRegime ?? null)
  const isIndia = regime.code === INDIA_GST

  const registration = watch('tax_registration_id')
  const fromRegistration = stateOfRegistration(regime.code, registration)

  // Where the registration proves the state, following it as the number is typed is
  // friendlier than letting the server refuse the pair afterwards.
  useEffect(() => {
    if (fromRegistration) setValue('state_code', fromRegistration, { shouldDirty: true })
  }, [fromRegistration, setValue])

  const guard = useDirtyGuard(isDirty)

  const save = useMutation({
    mutationFn: (values: FormValues) => updateCompanySettings(toPayload(values)),
    onSuccess: (saved) => {
      setFormError(null)
      queryClient.setQueryData(['company-settings'], saved)
      reset(valuesOf(saved))
      toast.success('Company details saved. The next document uses them.')
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? error.message
          : 'Could not reach the server. Nothing was changed.',
      )
    },
  })

  const logo = useMutation({
    mutationFn: async (file: File) => setCompanyLogo(await uploadFile(file, 'company_logo')),
    onSuccess: (saved) => {
      queryClient.setQueryData(['company-settings'], saved)
      toast.success('Logo updated.')
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'The logo could not be uploaded.')
    },
    onSettled: () => {
      // Clear the picker either way, so choosing the same file again still fires a change.
      if (logoInput.current) logoInput.current.value = ''
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    return save.mutateAsync(values).catch(() => {
      /* surfaced in formError by onError */
    })
  })

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Company settings</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {canEdit
            ? 'Copied onto every document raised from now on. Documents already issued keep what they were printed with.'
            : 'How this company appears on its invoices. Editing needs system_settings:update.'}
        </p>
      </header>

      <QueryState isLoading={query.isLoading} error={query.error}>
        {settings && (
          <form onSubmit={onSubmit} noValidate className="space-y-4">
            {formError && <Alert tone="error">{formError}</Alert>}

            <Card>
              <CardHeader
                title="Identity"
                description={`The registered name and the ${regime.registration_label} it bills under.`}
              />
              <CardBody className="grid gap-4 sm:grid-cols-2">
                <Field label="Registered name" error={errors.legal_name?.message}>
                  {(props) => <Input {...props} {...register('legal_name')} disabled={!canEdit} />}
                </Field>

                <Field
                  label="Trading name"
                  error={errors.trade_name?.message}
                  hint="Printed on the invoice if set."
                >
                  {(props) => <Input {...props} {...register('trade_name')} disabled={!canEdit} />}
                </Field>

                <Field
                  label="Tax regime"
                  error={errors.tax_regime?.message}
                  hint="Which country's tax rules this deployment bills under. Documents already issued keep the rules they were raised under."
                >
                  {(props) => (
                    <select
                      {...props}
                      {...register('tax_regime')}
                      disabled={!canEdit}
                      className={SELECT}
                    >
                      {(regime.available.length
                        ? regime.available
                        : [
                            {
                              code: regime.code,
                              name: regime.name,
                              country: regime.country,
                              currency: regime.currency,
                            },
                          ]
                      ).map((option) => (
                        <option key={option.code} value={option.code}>
                          {option.name} ({option.currency})
                        </option>
                      ))}
                    </select>
                  )}
                </Field>

                <Field
                  label={regime.registration_label}
                  error={errors.tax_registration_id?.message}
                  hint={
                    isIndia
                      ? 'Leave blank if the business is not GST registered — it then issues a bill of supply.'
                      : 'Leave blank if the business is not registered to collect sales tax.'
                  }
                >
                  {(props) => (
                    <Input
                      {...props}
                      {...register('tax_registration_id')}
                      disabled={!canEdit}
                      placeholder={isIndia ? '27AAPFU0939F1ZV' : '12-3456789'}
                      className="font-mono uppercase"
                    />
                  )}
                </Field>

                {isIndia && (
                  <Field label="PAN" error={errors.pan?.message}>
                    {(props) => (
                      <Input
                        {...props}
                        {...register('pan')}
                        disabled={!canEdit}
                        placeholder="AAPFU0939F"
                        className="font-mono uppercase"
                      />
                    )}
                  </Field>
                )}

                <Field
                  label="State of registration"
                  error={errors.state_code?.message}
                  hint={
                    fromRegistration
                      ? `Read from the ${regime.registration_label}, which is the registered fact.`
                      : 'Decides whether a sale is taxed as within the state or across a state line.'
                  }
                >
                  {(props) => (
                    <select
                      {...props}
                      {...register('state_code')}
                      disabled={!canEdit || Boolean(fromRegistration)}
                      className={SELECT}
                    >
                      <option value="">Not set</option>
                      {regime.states.map((state) => (
                        <option key={state.code} value={state.code}>
                          {state.name} ({state.code})
                        </option>
                      ))}
                    </select>
                  )}
                </Field>

                <Field
                  label="Currency"
                  error={errors.currency?.message}
                  hint={`${regime.currency} in ${regime.country}.`}
                >
                  {(props) => (
                    <Input
                      {...props}
                      {...register('currency')}
                      disabled={!canEdit}
                      className="font-mono uppercase"
                    />
                  )}
                </Field>
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="Address and contact"
                description="The address on the letterhead."
              />
              <CardBody className="grid gap-4 sm:grid-cols-2">
                <Field label="Address line 1" error={errors.address_line1?.message}>
                  {(props) => (
                    <Input {...props} {...register('address_line1')} disabled={!canEdit} />
                  )}
                </Field>
                <Field label="Address line 2" error={errors.address_line2?.message}>
                  {(props) => (
                    <Input {...props} {...register('address_line2')} disabled={!canEdit} />
                  )}
                </Field>
                <Field label="City" error={errors.city?.message}>
                  {(props) => <Input {...props} {...register('city')} disabled={!canEdit} />}
                </Field>
                <Field label="PIN code" error={errors.postal_code?.message}>
                  {(props) => <Input {...props} {...register('postal_code')} disabled={!canEdit} />}
                </Field>
                <Field label="Country" error={errors.country?.message}>
                  {(props) => <Input {...props} {...register('country')} disabled={!canEdit} />}
                </Field>
                <Field label="Email" error={errors.email?.message}>
                  {(props) => (
                    <Input {...props} {...register('email')} type="email" disabled={!canEdit} />
                  )}
                </Field>
                <Field label="Phone" error={errors.phone?.message}>
                  {(props) => (
                    <Input {...props} {...register('phone')} type="tel" disabled={!canEdit} />
                  )}
                </Field>
                <Field label="Website" error={errors.website?.message}>
                  {(props) => <Input {...props} {...register('website')} disabled={!canEdit} />}
                </Field>
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="Invoice wording"
                description="Terms, footer and bank details, as they appear on the document."
              />
              <CardBody className="space-y-4">
                <Field label="Default terms" error={errors.invoice_terms?.message}>
                  {(props) => (
                    <textarea
                      {...props}
                      {...register('invoice_terms')}
                      rows={3}
                      disabled={!canEdit}
                      className={TEXTAREA}
                    />
                  )}
                </Field>
                <Field
                  label="Footer"
                  error={errors.invoice_footer?.message}
                  hint="The last line on the page — a declaration or a thank you."
                >
                  {(props) => (
                    <textarea
                      {...props}
                      {...register('invoice_footer')}
                      rows={2}
                      disabled={!canEdit}
                      className={TEXTAREA}
                    />
                  )}
                </Field>
                <Field
                  label="Bank details"
                  error={errors.bank_details?.message}
                  hint="Where a customer paying by transfer should send it."
                >
                  {(props) => (
                    <textarea
                      {...props}
                      {...register('bank_details')}
                      rows={3}
                      disabled={!canEdit}
                      className={TEXTAREA}
                    />
                  )}
                </Field>
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="Commission"
                description="The house rate. A rep with their own rate on their account overrides it."
              />
              <CardBody className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Rate (%)"
                  error={errors.commission_rate_pct?.message}
                  hint="0 turns commission off."
                >
                  {(props) => (
                    <Input
                      {...props}
                      {...register('commission_rate_pct')}
                      inputMode="decimal"
                      disabled={!canEdit}
                      className="tabular-nums"
                    />
                  )}
                </Field>
                <Field label="Earned" error={errors.commission_accrual?.message}>
                  {(props) => (
                    <select
                      {...props}
                      {...register('commission_accrual')}
                      disabled={!canEdit}
                      className={SELECT}
                    >
                      {COMMISSION_ACCRUALS.map((accrual) => (
                        <option key={accrual} value={accrual}>
                          {ACCRUAL_LABELS[accrual]}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
                <p className="text-sm text-slate-500 sm:col-span-2 dark:text-slate-400">
                  At this rate, {formatMoney('100000')} of sales earns{' '}
                  {formatMoney(String((Number(watch('commission_rate_pct') || 0) * 100000) / 100))}.
                </p>
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="Default delivery charge"
                description="For all locations: what an online or sales order pays to be delivered when its location has no charge of its own (below). Flat, and outside the goods total. POS never charges delivery."
              />
              <CardBody className="grid gap-4 sm:grid-cols-2">
                <Field
                  label={`Delivery charge (${regime.currency})`}
                  error={errors.shipping_flat_rate?.message}
                  hint="0 delivers free on every order."
                >
                  {(props) => (
                    <Input
                      {...props}
                      {...register('shipping_flat_rate')}
                      inputMode="decimal"
                      disabled={!canEdit}
                      className="tabular-nums"
                    />
                  )}
                </Field>
                <Field
                  label={`Free above (${regime.currency})`}
                  error={errors.shipping_free_over?.message}
                  hint="Leave empty to charge delivery on every order."
                >
                  {(props) => (
                    <Input
                      {...props}
                      {...register('shipping_free_over')}
                      inputMode="decimal"
                      disabled={!canEdit}
                      className="tabular-nums"
                      placeholder="No threshold"
                    />
                  )}
                </Field>
                <p className="text-sm text-slate-500 sm:col-span-2 dark:text-slate-400">
                  {Number(watch('shipping_flat_rate') || 0) === 0
                    ? 'By default, delivery is free.'
                    : watch('shipping_free_over').trim() === ''
                      ? `By default, every order pays ${formatMoney(watch('shipping_flat_rate').trim() || '0')} for delivery.`
                      : `By default, orders pay ${formatMoney(watch('shipping_flat_rate').trim() || '0')}, waived from ${formatMoney(watch('shipping_free_over').trim())} of goods.`}
                </p>
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="Logo"
                description="Shown on the invoice PDF. PNG or JPEG, and small enough to print."
              />
              <CardBody className="flex flex-wrap items-center gap-4">
                {settings.logo_url ? (
                  <img
                    src={apiUrl(settings.logo_url)}
                    alt="Company logo"
                    className="h-16 w-auto rounded border border-slate-200 bg-white p-1 dark:border-slate-700"
                  />
                ) : (
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    No logo yet. The PDF prints the company name instead.
                  </span>
                )}

                {canEdit && (
                  <>
                    <input
                      ref={logoInput}
                      type="file"
                      accept="image/png,image/jpeg"
                      className="sr-only"
                      aria-label="Choose a logo file"
                      onChange={(event) => {
                        const file = event.target.files?.[0]
                        if (file) logo.mutate(file)
                      }}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      loading={logo.isPending}
                      onClick={() => logoInput.current?.click()}
                    >
                      {settings.logo_key ? 'Replace logo' : 'Upload logo'}
                    </Button>
                  </>
                )}
              </CardBody>
            </Card>

            {canEdit && (
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!isDirty || isSubmitting}
                  onClick={() => reset(valuesOf(settings))}
                >
                  Reset
                </Button>
                <Button type="submit" disabled={!isDirty} loading={isSubmitting}>
                  Save changes
                </Button>
              </div>
            )}
          </form>
        )}
      </QueryState>

      {settings && <DeliveryRates canEdit={canEdit} />}

      <UnsavedChangesDialog guard={guard} />
    </div>
  )
}
